import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SmartGridComponent, ColDef, GridConfig, SortChangedEvent, FilterChangedEvent, PageChangedEvent } from 'app/shared/components/smart-grid';
import { MockOrder, MOCK_ORDERS } from '../mock-data/mock-orders.data';

/**
 * API Mode Example
 * Demonstrates server-side operations (pagination, sorting, filtering, search)
 */
@Component({
  selector: 'app-api-mode-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Modo API (Server-Side Operations)</h2>
        <p class="text-gray-600 mb-2">
          En modo API, todas las operaciones (paginación, ordenamiento, filtrado, búsqueda) se realizan en el servidor.
          El grid emite eventos que el componente padre debe escuchar y responder con nuevos datos.
        </p>
        <div class="flex gap-2 flex-wrap mb-4">
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            🌐 API Mode
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            📄 Server-Side Pagination
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            ⬆️⬇️ Server-Side Sorting
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            🔍 Server-Side Filtering
          </span>
          <span class="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold">
            🔎 Server-Side Search
          </span>
        </div>

        <!-- API Request Info Card -->
        <div class="p-4 bg-blue-50 dark:bg-blue-900 rounded mb-4">
          <h3 class="font-semibold mb-2 flex items-center gap-2">
            <mat-icon class="text-blue-600">api</mat-icon>
            Última petición simulada al servidor:
          </h3>
          <div class="bg-white dark:bg-gray-800 p-3 rounded font-mono text-xs overflow-x-auto">
            <pre>{{ lastApiRequest | json }}</pre>
          </div>
          <div class="mt-2 text-sm">
            <strong>Total de registros:</strong> {{ totalRecords }} |
            <strong>Mostrando:</strong> {{ displayedOrders.length }} registros |
            <strong>Página:</strong> {{ currentPage + 1 }} de {{ totalPages }}
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          #grid
          [data]="displayedOrders"
          [columnDefs]="columnDefs"
          [config]="gridConfig"
          [loading]="loading"
          [totalRecords]="totalRecords"
          (pageChanged)="onPageChanged($event)"
          (sortChanged)="onSortChanged($event)"
          (filterChanged)="onFilterChanged($event)"
          (searchChanged)="onSearchChanged($event)">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">✨ Características del Modo API:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li><strong>Paginación del servidor:</strong> Solo se cargan los registros de la página actual</li>
          <li><strong>Ordenamiento del servidor:</strong> La BD ordena los datos antes de enviarlos</li>
          <li><strong>Filtrado del servidor:</strong> Los filtros se aplican en la consulta SQL/query</li>
          <li><strong>Búsqueda del servidor:</strong> La búsqueda se realiza en el backend</li>
          <li><strong>Mejor rendimiento:</strong> Ideal para datasets grandes (millones de registros)</li>
          <li><strong>Menor uso de memoria:</strong> Solo se mantienen en memoria los registros visibles</li>
          <li><strong>Latencia simulada:</strong> Este demo simula 500ms de latencia de red</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">🎯 Flujo de trabajo en modo API:</h3>
        <ol class="text-sm space-y-2 list-decimal list-inside">
          <li><strong>Usuario interactúa:</strong> Cambia página, ordena columna, aplica filtro, o busca</li>
          <li><strong>Grid emite evento:</strong> <code>pageChanged</code>, <code>sortChanged</code>, <code>filterChanged</code>, o <code>searchChanged</code></li>
          <li><strong>Componente escucha:</strong> Recibe el evento y construye los parámetros de la petición</li>
          <li><strong>Petición HTTP:</strong> Envía petición al backend con los parámetros (página, orden, filtros, búsqueda)</li>
          <li><strong>Servidor responde:</strong> Devuelve los datos filtrados/ordenados y el total de registros</li>
          <li><strong>Grid se actualiza:</strong> Asigna los nuevos datos y actualiza el paginador</li>
        </ol>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📝 Código de este ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{exampleCode}}</code></pre>
      </div>
    </div>
  `,
})
export class ApiModeExampleComponent {
  @ViewChild('grid') grid!: SmartGridComponent<MockOrder>;

  // All orders (simulating server database)
  private allOrders: MockOrder[] = MOCK_ORDERS;

  // Currently displayed orders (what the server would return)
  displayedOrders: MockOrder[] = [];

  // Loading state
  loading: boolean = false;

  // Pagination state
  currentPage: number = 0;
  pageSize: number = 10;
  totalRecords: number = 0;
  totalPages: number = 0;

  // Last API request (for display)
  lastApiRequest: any = {};

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nro. Documento',
      width: 180,
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 300,
    },
    {
      field: 'class.name',
      headerName: 'Clase',
      width: 150,
      filterable: true,
    },
    {
      field: 'soldTo.address.city',
      headerName: 'Ciudad',
      width: 150,
      filterable: true,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 150,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'deliveryDateTime',
      headerName: 'Fecha de Entrega',
      width: 180,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    // API MODE ENABLED
    pagination: {
      enabled: true,
      mode: 'api', // SERVER-SIDE PAGINATION
      pageSize: 10,
      pageSizeOptions: [5, 10, 20, 50],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: 'api', // SERVER-SIDE SORTING
      multiColumn: false,
    },
    columnFilters: {
      enabled: true,
      mode: 'api', // SERVER-SIDE FILTERING
    },
    search: {
      enabled: true,
      mode: 'api', // SERVER-SIDE SEARCH
      showSearchInput: true,
      placeholder: 'Buscar en el servidor...',
      searchableFields: ['serial', 'soldTo.name', 'soldTo.address.city'],
    },
    styling: {
      maxHeight: '600px',
    },
  };

  // Current server-side state
  private serverState = {
    pageIndex: 0,
    pageSize: 10,
    sortField: null as string | null,
    sortDirection: null as 'asc' | 'desc' | null,
    filters: {} as Record<string, any[]>,
    searchTerm: '',
  };

  constructor() {
    // Initial load
    this.loadDataFromServer();
  }

  /**
   * Simulate server-side data loading
   */
  private loadDataFromServer(): void {
    this.loading = true;

    // Build API request object (for display)
    this.lastApiRequest = {
      endpoint: '/api/orders/filter',
      method: 'GET',
      params: {
        page: this.serverState.pageIndex,
        pageSize: this.serverState.pageSize,
        sortBy: this.serverState.sortField,
        sortOrder: this.serverState.sortDirection,
        filters: this.serverState.filters,
        search: this.serverState.searchTerm,
      },
      timestamp: new Date().toISOString(),
    };

    // Simulate network latency (500ms)
    setTimeout(() => {
      // Start with all orders
      let filteredOrders = [...this.allOrders];

      // Apply search
      if (this.serverState.searchTerm) {
        const searchLower = this.serverState.searchTerm.toLowerCase();
        filteredOrders = filteredOrders.filter(order =>
          order.serial.toLowerCase().includes(searchLower) ||
          order.soldTo.name.toLowerCase().includes(searchLower) ||
          order.soldTo.address.city.toLowerCase().includes(searchLower)
        );
      }

      // Apply column filters
      Object.entries(this.serverState.filters).forEach(([field, values]) => {
        if (values && values.length > 0) {
          filteredOrders = filteredOrders.filter(order => {
            const orderValue = this.getNestedValue(order, field);
            return values.includes(orderValue);
          });
        }
      });

      // Apply sorting
      if (this.serverState.sortField && this.serverState.sortDirection) {
        filteredOrders.sort((a, b) => {
          const aValue = this.getNestedValue(a, this.serverState.sortField!);
          const bValue = this.getNestedValue(b, this.serverState.sortField!);

          let comparison = 0;
          if (aValue < bValue) comparison = -1;
          if (aValue > bValue) comparison = 1;

          return this.serverState.sortDirection === 'asc' ? comparison : -comparison;
        });
      }

      // Calculate pagination
      this.totalRecords = filteredOrders.length;
      this.totalPages = Math.ceil(this.totalRecords / this.serverState.pageSize);

      // Get page data
      const startIndex = this.serverState.pageIndex * this.serverState.pageSize;
      const endIndex = startIndex + this.serverState.pageSize;
      this.displayedOrders = filteredOrders.slice(startIndex, endIndex);

      this.currentPage = this.serverState.pageIndex;
      this.pageSize = this.serverState.pageSize;

      this.loading = false;
    }, 500); // Simulate 500ms network latency
  }

  /**
   * Handle page change event (server-side pagination)
   */
  onPageChanged(event: PageChangedEvent): void {

    this.serverState.pageIndex = event.pageIndex;
    this.serverState.pageSize = event.pageSize;

    // Load new page from server
    this.loadDataFromServer();
  }

  /**
   * Handle sort change event (server-side sorting)
   */
  onSortChanged(event: SortChangedEvent<MockOrder>): void {

    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0]; // Single column sort
      this.serverState.sortField = sortState.field as string;
      // Only set direction if it's 'asc' or 'desc', not empty string
      this.serverState.sortDirection = (sortState.direction === 'asc' || sortState.direction === 'desc')
        ? sortState.direction
        : null;
    } else {
      this.serverState.sortField = null;
      this.serverState.sortDirection = null;
    }

    // Reset to first page when sorting changes
    this.serverState.pageIndex = 0;

    // Load sorted data from server
    this.loadDataFromServer();
  }

  /**
   * Handle filter change event (server-side filtering)
   */
  onFilterChanged(event: FilterChangedEvent<MockOrder>): void {

    this.serverState.filters = event.filterState as Record<string, any[]>;

    // Reset to first page when filters change
    this.serverState.pageIndex = 0;

    // Load filtered data from server
    this.loadDataFromServer();
  }

  /**
   * Handle search change event (server-side search)
   */
  onSearchChanged(event: { searchTerm: string; previousTerm: string }): void {

    this.serverState.searchTerm = event.searchTerm;

    // Reset to first page when search changes
    this.serverState.pageIndex = 0;

    // Load searched data from server
    this.loadDataFromServer();
  }

  /**
   * Helper to get nested value from object
   */
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  }

  exampleCode = `// Component Code
import { Component } from '@angular/core';
import { SmartGridComponent, GridConfig } from 'app/shared/components/smart-grid';

export class ApiModeExampleComponent {
  displayedOrders: Order[] = [];
  loading: boolean = false;
  totalRecords: number = 0;    // ⚠️ IMPORTANTE: Total de registros del servidor

  gridConfig: GridConfig<Order> = {
    pagination: {
      enabled: true,
      mode: 'api',           // ⚠️ API MODE ENABLED
      pageSize: 10,
    },
    sorting: {
      enabled: true,
      mode: 'api',           // ⚠️ API MODE ENABLED
    },
    columnFilters: {
      enabled: true,
      mode: 'api',           // ⚠️ API MODE ENABLED
    },
    search: {
      enabled: true,
      mode: 'api',           // ⚠️ API MODE ENABLED
    },
  };

  // Listen to grid events and fetch data from server
  onPageChanged(event: PageChangedEvent): void {
    this.fetchFromServer({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  onSortChanged(event: SortChangedEvent): void {
    this.fetchFromServer({
      sortBy: event.sortStates[0]?.field,
      sortOrder: event.sortStates[0]?.direction,
    });
  }

  onFilterChanged(event: FilterChangedEvent): void {
    this.fetchFromServer({
      filters: event.filterState,
    });
  }

  onSearchChanged(event: { searchTerm: string }): void {
    this.fetchFromServer({
      search: event.searchTerm,
    });
  }

  // Make HTTP request to backend
  private fetchFromServer(params: any): void {
    this.loading = true;

    this.http.get('/api/orders', { params }).subscribe(response => {
      this.displayedOrders = response.data;
      this.totalRecords = response.total;  // ⚠️ CRUCIAL: Actualizar el total
      this.loading = false;
    });
  }
}

// Template
<app-smart-grid
  [data]="displayedOrders"
  [columnDefs]="columnDefs"
  [config]="gridConfig"
  [loading]="loading"
  [totalRecords]="totalRecords"          ⚠️ IMPORTANTE: Pasar el total
  (pageChanged)="onPageChanged($event)"
  (sortChanged)="onSortChanged($event)"
  (filterChanged)="onFilterChanged($event)"
  (searchChanged)="onSearchChanged($event)">
</app-smart-grid>

// Backend API Response Format:
{
  data: Order[],           // Current page data (ej: 10 registros)
  total: number,           // Total records matching filters (ej: 120)
  page: number,            // Current page index
  pageSize: number         // Page size
}

// ⚠️ CLAVE PARA EL MODO API:
// 1. El backend solo devuelve los datos de la página actual
// 2. Pero DEBE devolver el total de registros que coinciden con los filtros
// 3. El componente debe pasar este total al grid via [totalRecords]
// 4. El grid usa este total para renderizar correctamente el paginador`;
}
